import {html, css} from 'lit'
import {customElement, property, query, state} from 'lit/decorators.js'
import {BaseComponent} from './base-component.js'
import {classMap} from 'lit-html/directives/class-map.js'

const TOOLTIP_DISTANCE = 12
const SCREEN_PADDING = 8

export enum TOOLTIP_POSITIONS {
  top = 'top',
  bot = 'bot',
}

export enum ARROW_POSITIONS {
  left = 'left',
  center = 'center',
  right = 'right',
}

export enum TOOLTIP_VARIANTS {
  default = 'default',
  info = 'info',
  danger = 'danger',
  legacy = 'legacy',
}

@customElement('il-tooltip')
export default class TooltipComponent extends BaseComponent {
  static override styles = [
    BaseComponent.styles,
    css`
      :host([fill]) {
        width: 100%;
      }

      .il-tooltip {
        position: relative;
        width: fit-content;
        display: flex;

        --il-tooltip-bg-color: var(--il-color-accent-50);
        --il-tooltip-border-color: var(--il-color-accent-100);
        --il-tooltip-text-color: inherit;
        --il-tooltip-border-radius: 12px;
        --il-tooltip-hitbox-size: 20px;
        --il-tooltip-hitbox-y-adjust: -15px;
        --il-tooltip-content-max-width: 350px;
        --il-tooltip-content-border-width: 2px;
        --il-tooltip-content-arrow-padding: 6px;
        --il-tooltip-content-arrow-y-adjust: -8px; /* padding + border width */
        --il-tooltip-content-x-position: -50%;
        --il-tooltip-content-y-position: -100%;
        --il-tooltip-content-arrow-edge-offset: 20px;
      }

      .il-tooltip.il-tooltip--fill {
        width: 100%;
      }

      .il-tooltip.il-tooltip--info {
        --il-tooltip-bg-color: var(--il-theme-indigo-color-accent-50);
        --il-tooltip-border-color: var(--il-theme-indigo-color-accent-100);
      }

      .il-tooltip.il-tooltip--danger {
        --il-tooltip-bg-color: var(--il-theme-bright-pink-color-accent-50);
        --il-tooltip-border-color: var(--il-theme-bright-pink-color-accent-100);
      }

      .il-tooltip.il-tooltip--legacy {
        --il-tooltip-bg-color: #3A3C46;
        --il-tooltip-border-color: #3A3C46;
        --il-tooltip-text-color: #fff;
        --il-tooltip-border-radius: 0px;
      }

      .il-tooltip__trigger {
        cursor: pointer;
      }

      .il-tooltip.il-tooltip--fill .il-tooltip__trigger {
        width: 100%;
      }

      .il-tooltip__content-wrapper {
        position: fixed;
        width: 0;
        height: 0;
        z-index: var(--il-tooltip-index);
      }

      /* START: Tooltip content styles */
      .il-tooltip__content {
        visibility: hidden;
        position: relative;
        padding: 12px 16px;
        border-radius: var(--il-tooltip-border-radius);
        border: var(--il-tooltip-content-border-width) solid var(--il-tooltip-border-color);
        background: var(--il-tooltip-bg-color);
        color: var(--il-tooltip-text-color);
        z-index: 1;
        width: max-content;
        min-width: calc(2 * var(--il-tooltip-content-arrow-edge-offset));
        max-width: var(--il-tooltip-content-max-width);
        box-shadow: var(--il-shadow-cool-base);
        left: 50%;
        top: 50%;
        transform: translate(var(--il-tooltip-content-x-position), var(--il-tooltip-content-y-position));
      }

      .il-tooltip.il-tooltip--bot .il-tooltip__content {
        --il-tooltip-content-y-position: 0;
      }

      .il-tooltip.il-tooltip--left .il-tooltip__content {
        --il-tooltip-content-x-position: calc(-1 * var(--il-tooltip-content-arrow-edge-offset));
      }

      .il-tooltip.il-tooltip--right .il-tooltip__content {
        --il-tooltip-content-x-position: calc(-100% + var(--il-tooltip-content-arrow-edge-offset));
      }

      .il-tooltip.il-tooltip--open .il-tooltip__content {
        visibility: visible;
      }

      /* START: Arrow styles */
      .il-tooltip__content-wrapper .il-tooltip__content-arrow {
        visibility: hidden;
        border-width: var(--il-tooltip-content-border-width);
        border-style: solid;
        border-color: var(--il-tooltip-border-color);
        background: var(--il-tooltip-bg-color);
        position: absolute;
        padding: var(--il-tooltip-content-arrow-padding);
        left: 50%;
        z-index: 1;
      }

      .il-tooltip.il-tooltip--top .il-tooltip__content-wrapper .il-tooltip__content-arrow {
        bottom: var(--il-tooltip-content-arrow-y-adjust);
        border-top-color: var(--il-tooltip-bg-color);
        border-left-color: var(--il-tooltip-bg-color);
        transform: translate(-50%, -2px) rotate(45deg);
      }

      .il-tooltip.il-tooltip--bot .il-tooltip__content-wrapper .il-tooltip__content-arrow {
        top: var(--il-tooltip-content-arrow-y-adjust);
        border-bottom-color: var(--il-tooltip-bg-color);
        border-right-color: var(--il-tooltip-bg-color);
        transform: translate(-50%, 2px) rotate(45deg);
      }

      .il-tooltip.il-tooltip--open .il-tooltip__content-arrow {
        visibility: visible;
      }
      /* END: Arrow styles */

      /* START: hitbox pseudo element to allow mouse hovering without closing the tooltip */
      .il-tooltip__content:before {
        content: '';
        position: absolute;
        left: 50%;
        width: var(--il-tooltip-content-max-width);
        height: var(--il-tooltip-hitbox-size);
        transform: translateX(-50%);
        z-index: var(--il-tooltip-index);
      }

      .il-tooltip.il-tooltip--top .il-tooltip__content::before {
        bottom: var(--il-tooltip-hitbox-y-adjust);
      }

      .il-tooltip.il-tooltip--bot .il-tooltip__content::before {
        top: var(--il-tooltip-hitbox-y-adjust);
      }
      /* END: hitbox pseudo element to allow mouse hovering without closing the tooltip */
      /* END: Tooltip content styles */
    `,
  ]

  @property({type: Boolean, reflect: true})
  fill: boolean = false

  @property({attribute: 'close-timeout', type: Number})
  closeTimeout: number = 0

  @property({attribute: 'tooltip-text', type: String})
  tooltipText: string = ''

  @property({attribute: 'tooltip-position', type: String})
  tooltipPosition: TOOLTIP_POSITIONS = TOOLTIP_POSITIONS.top

  @property({attribute: 'arrow-position', type: String})
  arrowPosition: ARROW_POSITIONS = ARROW_POSITIONS.center

  @property({attribute: 'tooltip-variant', type: String})
  tooltipVariant: TOOLTIP_VARIANTS = TOOLTIP_VARIANTS.default

  @property({attribute: 'onhover', type: Object})
  onHover: Function = () => {}

  @property({attribute: 'disable-content-hovering', type: Boolean})
  disableContentHovering: boolean = false

  @query('#trigger')
  trigger: HTMLElement | undefined

  @query('#tooltip-wrapper')
  tooltipWrapper: HTMLElement | undefined

  @state()
  isHover: boolean = false

  @state()
  isFocused: boolean = false

  @state()
  private _tooltipPosition: TOOLTIP_POSITIONS = this.tooltipPosition

  @state()
  private _closeTimeout?: number

  private _scrollableParent: Element | Window | null = null

  private findScrollableParent = (element: Element): Element | Window => {
    let current: Element | null = element.parentElement

    while (current) {
      const style = window.getComputedStyle(current)
      const overflow = style.overflow || style.overflowY || style.overflowX

      if (overflow === 'auto' || overflow === 'scroll') {
        return current
      }

      current = current.parentElement
    }

    return window
  }

  override connectedCallback (): void {
    super.connectedCallback()
    this._tooltipPosition = this.tooltipPosition
  }

  override disconnectedCallback() {
    super.disconnectedCallback()

    this._closeTimeout && clearInterval(this._closeTimeout)
  }

  private calculateTooltipPosition = (): void => {
    if (this.tooltipWrapper && this.trigger) {
      const {top, bottom, left, width} = this.trigger.getBoundingClientRect()

      this.tooltipWrapper.style.left = `${left + width / 2}px` //+ width / 2 helps with the centering of the tooltip

      if (this._tooltipPosition === TOOLTIP_POSITIONS.top) {
        this.tooltipWrapper.style.top = `${top - TOOLTIP_DISTANCE}px`
      } else {
        this.tooltipWrapper.style.top = `${bottom + TOOLTIP_DISTANCE}px`
      }
    }
  }

  private preventHorizontalOverflow = (): void => {
    if (this.tooltipWrapper && this.trigger) {
      const tooltipContent: HTMLElement | null =
        this.tooltipWrapper?.querySelector('[data-test-iliad-tooltip-content]')

      if (tooltipContent) {
        const contentRects = tooltipContent.getBoundingClientRect()
        let overflowX = ''

        if (contentRects.right > window.innerWidth) {
          const _overflowX =
            Math.abs(window.innerWidth - contentRects.right) + SCREEN_PADDING

          overflowX = `-${_overflowX}px`
          tooltipContent.style.left = `-${overflowX}px`
        }

        if (contentRects.left < SCREEN_PADDING) {
          const _overflowX = Math.abs(contentRects.left) + SCREEN_PADDING

          overflowX = `${_overflowX}px`
        }

        tooltipContent.style.left = overflowX
      }
    }
  }

  private updatePositionOnScroll = (): void => {
    const tooltipContent = this.tooltipWrapper?.querySelector(
      '[data-test-iliad-tooltip-content]'
    )

    const triggerRect = this.trigger?.getBoundingClientRect()
    const tooltipContentRect = tooltipContent?.getBoundingClientRect()
    const tooltipHeight = tooltipContentRect?.height ?? 0

    if (this.tooltipPosition === TOOLTIP_POSITIONS.bot) {
      const triggerBot = triggerRect?.bottom ?? 0
      const tooltipContentSpace = triggerBot + tooltipHeight + SCREEN_PADDING
      const shouldChangeBotPosition = window.innerHeight < tooltipContentSpace

      this._tooltipPosition = shouldChangeBotPosition
        ? TOOLTIP_POSITIONS.top
        : TOOLTIP_POSITIONS.bot
    } else if (this.tooltipPosition === TOOLTIP_POSITIONS.top) {
      const triggerTop = triggerRect?.top ?? 0
      const tooltipContentSpace = tooltipHeight + SCREEN_PADDING
      const shouldChangeTopPosition = triggerTop < tooltipContentSpace

      this._tooltipPosition = shouldChangeTopPosition
        ? TOOLTIP_POSITIONS.bot
        : TOOLTIP_POSITIONS.top
    }

    this.calculateTooltipPosition()
  }

  private resetTooltipPosition = (): void => {
    if (this.tooltipWrapper && this.trigger) {
      const tooltipContent: HTMLElement | null =
        this.tooltipWrapper?.querySelector('[data-test-iliad-tooltip-content]')

      if (tooltipContent) {
        tooltipContent.style.left = ''
      }
    }
  }

  private onOpenTooltip = (): void => {
    this.calculateTooltipPosition()

    this.preventHorizontalOverflow()

    this.updatePositionOnScroll()

    if (this.trigger) {
      this._scrollableParent = this.findScrollableParent(this.trigger)
      this._scrollableParent.addEventListener('scroll', this.updatePositionOnScroll, true)
    }

    // if there is a closeTimeout, set a timeout to automatically close this tooltip
    if (this.closeTimeout > 0) {
      if (this._closeTimeout) {
        clearTimeout(this._closeTimeout)
      }

      this._closeTimeout = setTimeout(() => {
        this.isHover = false
        this.isFocused = false

        this.onCloseTooltip()
      }, this.closeTimeout)
    }
  }

  private onCloseTooltip = (): void => {
    this.resetTooltipPosition()

    if (this._scrollableParent) {
      this._scrollableParent.removeEventListener('scroll', this.updatePositionOnScroll, true)
      this._scrollableParent = null
    }
  }

  private mouseenterTooltip = (disable?: boolean): void => {
    if (!disable) {
      this.isHover = true

      this.onOpenTooltip()
      this.onHover?.()
    }
  }

  private mouseleaveTooltip = (disable?: boolean): void => {
    if (!disable) {
      this.isHover = false
      this.onCloseTooltip()
    }
  }

  private focusinTooltip = (disable?: boolean): void => {
    if (!disable) {
      this.isFocused = true

      this.onOpenTooltip()
      this.onHover?.()
    }
  }

  private focusoutTooltip = (disable?: boolean): void => {
    if (!disable) {
      this.isFocused = false
      this.onCloseTooltip()
    }
  }

  // Tapping the trigger while it's already open only ever triggers `click`, since the
  // browser's synthetic mouseenter that normally opens it only fires on the element that
  // *changes* hover state. Handling `touchstart` (which fires ahead of that synthetic
  // sequence) lets us close it here and suppress the synthetic mouseenter/click so it
  // doesn't immediately reopen.
  private touchstartTrigger = (event: TouchEvent): void => {
    if (this.isHover || this.isFocused) {
      event.preventDefault()

      this.isHover = false
      this.isFocused = false

      this.onCloseTooltip()
    }
  }

  private get tooltipClasses (): {[x: string]: boolean | TOOLTIP_POSITIONS} {
    return {
      [`${this.baseCSSClass} il-tooltip`]: true,
      [`il-tooltip--${this._tooltipPosition}`]: this._tooltipPosition,
      [`il-tooltip--${this.tooltipVariant}`]:
        this.tooltipVariant !== TOOLTIP_VARIANTS.default,
      [`il-tooltip--${this.arrowPosition}`]:
        this.arrowPosition !== ARROW_POSITIONS.center,
      'il-tooltip--open': this.isHover || this.isFocused,
      'il-tooltip--fill': this.fill,
    }
  }

  override render () {
    const tooltipContent = (this.tooltipText) ? html`
      <div
        id="tooltip-wrapper"
        class="il-tooltip__content-wrapper"
        @mouseenter=${() => this.mouseenterTooltip(this.disableContentHovering)}
        @mouseleave=${() => this.mouseleaveTooltip(this.disableContentHovering)}
        @focusin=${() => this.focusinTooltip(this.disableContentHovering)}
        @focusout=${() => this.focusoutTooltip(this.disableContentHovering)}
      >
        <div
          class="il-tooltip__content il-font-paragraph-bold-14"
          data-test-iliad-tooltip-content
        >
          <slot name="content">
            ${this.tooltipText}
          </slot>
        </div>
        <div class="il-tooltip__content-arrow"></div>
      </div>
    ` : html``

    return html`
      <div
        class="${classMap(this.tooltipClasses)}"
        part="tooltip-content"
        data-test-iliad-tooltip
      >
        ${tooltipContent}

        <div
          class="il-tooltip__trigger"
          id="trigger"
          part="tooltip-trigger"
          aria-describedby="tooltip-wrapper"
          data-test-iliad-tooltip-trigger
          @mouseenter=${() => this.mouseenterTooltip(this.disableContentHovering)}
          @mouseleave=${() => this.mouseleaveTooltip(this.disableContentHovering)}
          @focusin=${() => this.focusinTooltip(this.disableContentHovering)}
          @focusout=${() => this.focusoutTooltip(this.disableContentHovering)}
          @touchstart=${this.touchstartTrigger}
        >
          <slot></slot>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'il-tooltip': TooltipComponent
  }
}
